import { formatWithCommas } from '@/utils/numberHelpers';
import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import type { CryptoRate } from '../../../../types/ApiTypes';
import Dashboard from "../../../layout/Dashboard";
import { cryptoService } from '../../../services/CryptoService';
import { ListHead, ListRow, Ticker } from "../../../styles/CrypoAssetStyle";
import { Content, Heading } from "../../../styles/OverviewStyle";
import { RFDesc, RFMain, RFTitle, RFWrap } from "../../../styles/RatesFeesStyle";
import { RatesNavigator, TabNavigator } from "../components/Navigator";


export default function Rates() {
   const [rates, setRates] = useState<CryptoRate[]>([])
   const [loading, setLoading] = useState(true)
   const [editingId, setEditingId] = useState<string | null>(null)
   const [editBuyValue, setEditBuyValue] = useState('')
   const [editSellValue, setEditSellValue] = useState('')

   useEffect(() => {
      loadRates()
   }, [])

   const loadRates = async () => {
      try {
         const response = await cryptoService.getCryptoRates()
         if (response.status === 'success' && response.data) {
            setRates(response.data)
         }
      } catch (error) {
         console.error('Failed to load rates:', error)
      } finally {
         setLoading(false)
      }
   }

   const onEdit = (currency: string, buyRate: string, sellRate: string) => {
      setEditingId(currency)
      setEditBuyValue(buyRate)
      setEditSellValue(sellRate)
   }

   const onCancel = () => {
      setEditingId(null)
      setEditBuyValue('')
      setEditSellValue('')
   }

   const onSave = async (currency: string) => {
      try {
         const buyRate = parseFloat(editBuyValue.replace(/[^0-9.]/g, ''))
         const sellRate = parseFloat(editSellValue.replace(/[^0-9.]/g, ''))
         if (isNaN(buyRate) || isNaN(sellRate)) return

         await Promise.all([
            cryptoService.updateBuyRate({ currency, rate: buyRate }),
            cryptoService.updateSellRate({ currency, rate: sellRate })
         ])
         await loadRates()
         setEditingId(null)
         setEditBuyValue('')
         setEditSellValue('')
      } catch (error) {
         console.error('Failed to update rates:', error)
      }
   }

   return (
      <Dashboard title="Rates & Fees / Rates / Buy & sell crypto" icon="solar:tag-price-linear" active="rates_fees">
         <Content>
            <Heading>Rates & Fees</Heading>

            <TabNavigator active="rates" />

            <RFWrap>
               <RatesNavigator active="index" />

               <RFMain>
                  <div>
                     <RFTitle>Buy & sell crypto</RFTitle>
                     <RFDesc>
                        The rates shown here are automatically pulled from your connected crypto exchange API. These values refresh in real time and cannot be edited manually. This ensures your users always see the most accurate buy and sell prices
                     </RFDesc>

                     <ListHead style={{ gridTemplateColumns: '1fr 140px 140px 200px 200px 100px' }}>
                        <div>Asset</div>
                        <div className="text-right pr-2">Quidax Buy Rate</div>
                        <div className="text-right pr-2">Quidax Sell Rate</div>
                        <div className="text-right pr-2">Buy Rate</div>
                        <div className="text-right pr-2">Sell Rate</div>
                        <div></div>
                     </ListHead>

                     {loading ? (
                        Array.from({ length: 5 }).map((_, i) => (
                           <ListRow key={i} style={{ gridTemplateColumns: '1fr 140px 140px 200px 200px 100px' }}>
                              <div className="flex items-center gap-3">
                                 <div className="w-5 h-5 rounded-full bg-gray-200 animate-pulse" />
                                 <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                                 <div className="h-4 w-12 bg-gray-200 rounded-full animate-pulse" />
                              </div>
                              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse ml-auto" />
                              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse ml-auto" />
                              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse ml-auto" />
                              <div className="h-4 w-20 bg-gray-200 rounded animate-pulse ml-auto" />
                              <div className="h-4 w-8 bg-gray-200 rounded animate-pulse ml-auto" />
                           </ListRow>
                        ))
                     ) : (
                        rates.map(rate => {
                           const isEditing = editingId === rate.currency

                           return (
                              <ListRow key={rate.currency} style={{ gridTemplateColumns: '1fr 140px 140px 200px 200px 100px' }}>
                                 <div className="flex items-center gap-3">
                                    <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-[#F1F1F3]">
                                       <img src={rate.logoUrl} alt={rate.name} className="w-5 h-5" />
                                    </span>
                                    <span className="text-sm text-[#101018]">{rate.name}</span>
                                    <Ticker>{rate.currency.toUpperCase()}</Ticker>
                                 </div>
                                 <div className="text-right text-sm text-[#6D6D72]">₦{formatWithCommas(rate.buyRateFromQuidax)}</div>
                                 <div className="text-right text-sm text-[#6D6D72]">₦{formatWithCommas(rate.sellRateFromQuidax)}</div>
                                 <div className="flex items-center justify-end">
                                    {isEditing ? (
                                       <input
                                          value={editBuyValue}
                                          onChange={e => setEditBuyValue(e.target.value)}
                                          className="w-[90px] h-7 px-2 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                                       />
                                    ) : (
                                       <span className="text-sm text-[#6D6D72]">₦{formatWithCommas(rate.buyRate)}</span>
                                    )}
                                 </div>
                                 <div className="flex items-center justify-end">
                                    {isEditing ? (
                                       <input
                                          value={editSellValue}
                                          onChange={e => setEditSellValue(e.target.value)}
                                          className="w-[90px] h-7 px-2 rounded-md border border-[#E7E7E9] bg-white text-sm text-[#101018] outline-none focus:border-[#AF7AD5]"
                                       />
                                    ) : (
                                       <span className="text-sm text-[#6D6D72]">₦{formatWithCommas(rate.sellRate)}</span>
                                    )}
                                 </div>
                                 <div className="flex items-center justify-end gap-2">
                                    {isEditing ? (
                                       <>
                                          <button aria-label="Cancel" onClick={onCancel} className="cursor-pointer">
                                             <Icon icon="mdi:close" width={16} className="text-[#DD524D] cursor-pointer" />
                                          </button>
                                          <button aria-label="Save" onClick={() => onSave(rate.currency)} className="cursor-pointer">
                                             <Icon icon="mdi:check" width={16} className="text-[#4CAF50] cursor-pointer" />
                                          </button>
                                       </>
                                    ) : (
                                       <button
                                          className="text-[#AF7AD5] text-xs hover:underline cursor-pointer"
                                          onClick={() => onEdit(rate.currency, rate.buyRate, rate.sellRate)}
                                       >
                                          Edit
                                       </button>
                                    )}
                                 </div>
                              </ListRow>
                           )
                        })
                     )}
                  </div>
               </RFMain>
            </RFWrap>
         </Content>
      </Dashboard>
   )
}
